import { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { View, Text, TouchableOpacity, ScrollView, StyleSheet, Platform, Modal, TextInput, ActivityIndicator } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation, NavigationProp } from '@react-navigation/native';
import { useTheme } from '../../../contexts/ThemeContext';
import { useLanguage } from '../../../contexts/LanguageContext';
import { FONTS } from '../../../constants/config';
import { createShadow } from '../../../utils/styles';
import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon, CheckIcon } from '../../../components/Icons';
import Toast from '../../../components/Toast';
import { talentTeachersApi, talentSpecializationsApi, talentProjectsApi, talentActivitiesApi } from '../../../services/apiService';
import { UNIQUE_SCHOOLS, UNIQUE_SPECIALIZATIONS } from '../mockData';
import { Teacher, TalentsFilters } from '../types';
import { MainStackParamList } from '../../../navigation/MainNavigator';

interface TeachersTableProps {
  readOnly?: boolean;
}

export default function TeachersTable({ readOnly }: TeachersTableProps = {}) {
  const { colors, isDark } = useTheme();
  const navigation = useNavigation<NavigationProp<MainStackParamList>>();
  const { t, language } = useLanguage();
  const isRTL = language === 'ar';

  const [currentPage, setCurrentPage] = useState(1);
  const [filterSchool, setFilterSchool] = useState('all');
  const [filterSpecialization, setFilterSpecialization] = useState('all');
  const [showSchoolDropdown, setShowSchoolDropdown] = useState(false);
  const [showSpecDropdown, setShowSpecDropdown] = useState(false);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc' | null>(null);
  const [toastVisible, setToastVisible] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState<'success' | 'error' | 'info'>('info');
  const [deleteTarget, setDeleteTarget] = useState<Teacher | null>(null);
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addForm, setAddForm] = useState({ name: '', specialization: '', school: '', email: '', phone: '' });
  const [addLoading, setAddLoading] = useState(false);
  const [viewTeacher, setViewTeacher] = useState<Teacher | null>(null);

  const pageSize = 10;
  const rtlRow = isRTL ? 'row-reverse' : 'row';

  // Fetch teachers: try API first, fall back to mock
  const fetchTeachers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await talentTeachersApi.list({ page_size: 1000 });
      const results = (res.results || []).map((t: any) => ({
        id: t.id,
        name: t.name,
        specialization: t.specialization_name || '',
        school: t.school || '',
        email: t.email || '',
        phone: t.phone || '',
        isVerified: t.is_verified,
        isActive: t.is_active,
        hasCertificate: t.has_certificate,
        projectNames: t.project_names || [],
      }));
      setTeachers(results);
    } catch (err) {
      console.log('[TeachersTable] API failed:', err);
      setTeachers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTeachers(); }, [fetchTeachers]);

  const toggleSort = () => {
    setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    setCurrentPage(1);
  };

  const filters: TalentsFilters = useMemo(() => ({
    school: filterSchool !== 'all' ? filterSchool : undefined,
    specialization: filterSpecialization !== 'all' ? filterSpecialization : undefined,
  }), [filterSchool, filterSpecialization]);

  // Filter, sort, and paginate teachers
  const data = useMemo(() => {
    let filtered = [...teachers];

    if (filters.school) filtered = filtered.filter(t => t.school === filters.school);
    if (filters.specialization) filtered = filtered.filter(t => t.specialization === filters.specialization);

    if (sortOrder) {
      filtered.sort((a, b) => {
        const cmp = a.name.localeCompare(b.name, 'ar');
        return sortOrder === 'asc' ? cmp : -cmp;
      });
    }

    const start = (currentPage - 1) * pageSize;
    return {
      results: filtered.slice(start, start + pageSize),
      count: filtered.length,
      page: currentPage,
      pageSize,
    };
  }, [teachers, currentPage, filters, sortOrder]);
  const totalPages = Math.max(1, Math.ceil(data.count / pageSize));

  const handleDeleteTeacher = (teacher: Teacher) => {
    setDeleteTarget(teacher);
  };

  const confirmDeleteTeacher = async () => {
    if (!deleteTarget) return;
    try {
      await talentTeachersApi.delete(deleteTarget.id);
      setToastMessage(`تم حذف "${deleteTarget.name}" بنجاح`);
      setToastType('success');
      setToastVisible(true);
      await fetchTeachers();
    } catch {
      setToastMessage('خطأ في حذف المنسق');
      setToastType('error');
      setToastVisible(true);
    }
    setDeleteTarget(null);
  };

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importing, setImporting] = useState(false);

  // Import wizard state
  const [importStep, setImportStep] = useState<'pick' | 'assign' | 'conflicts' | 'resolve'>('pick');
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importSpecId, setImportSpecId] = useState<number | undefined>(undefined);
  const [importProjId, setImportProjId] = useState<number | undefined>(undefined);
  const [importActId, setImportActId] = useState<number | undefined>(undefined);
  const [specOptions, setSpecOptions] = useState<{ id: number; name: string }[]>([]);
  const [projOptions, setProjOptions] = useState<{ id: number; name: string }[]>([]);
  const [actOptions, setActOptions] = useState<{ id: number; name: string }[]>([]);
  const [importConflicts, setImportConflicts] = useState<any[]>([]);
  const [importResult, setImportResult] = useState<any>(null);
  const [conflictResolutions, setConflictResolutions] = useState<Record<number, {
    selectedTeacherId: number | null;
    isNew: boolean;
    editedData: { name: string; eid: string; email: string; phone: string };
    hasEdited: boolean;
  }>>({});
  const [showSkipConfirm, setShowSkipConfirm] = useState(false);
  const [resolving, setResolving] = useState(false);

  const handleDownloadTemplate = () => {
    talentTeachersApi.downloadTemplate();
  };

  const handlePickFile = () => {
    if (Platform.OS === 'web' && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const resetImportWizard = () => {
    setImportStep('pick');
    setImportFile(null);
    setImportSpecId(undefined);
    setImportProjId(undefined);
    setImportActId(undefined);
    setSpecOptions([]);
    setProjOptions([]);
    setActOptions([]);
    setImportConflicts([]);
    setImportResult(null);
    setConflictResolutions({});
    setShowSkipConfirm(false);
    setResolving(false);
  };

  const onFileSelected = async (event: any) => {
    const file = event.target?.files?.[0];
    if (!file) return;
    setImportFile(file);
    try {
      const resp = await talentSpecializationsApi.list({ page_size: 200 });
      const items = resp?.results || resp || [];
      setSpecOptions(items.map((s: any) => ({ id: s.id, name: s.name })));
    } catch (e) { console.log('[TeachersTable] load specs failed:', e); }
    setImportStep('assign');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // When import spec changes, fetch projects
  const onImportSpecChange = async (specId: number | undefined) => {
    setImportSpecId(specId);
    setImportProjId(undefined);
    setImportActId(undefined);
    setProjOptions([]);
    setActOptions([]);
    if (!specId) return;
    try {
      const resp = await talentProjectsApi.list({ specialization: specId, page_size: 200 });
      const list = resp?.results || resp || [];
      setProjOptions(list.map((p: any) => ({ id: p.id, name: p.name })));
    } catch { setProjOptions([]); }
  };

  // When import project changes, fetch activities
  const onImportProjChange = async (projId: number | undefined) => {
    setImportProjId(projId);
    setImportActId(undefined);
    setActOptions([]);
    if (!projId) return;
    try {
      const resp = await talentActivitiesApi.list({ project: projId, page_size: 200 });
      const list = resp?.results || resp || [];
      setActOptions(list.map((a: any) => ({ id: a.id, name: a.name })));
    } catch { setActOptions([]); }
  };

  // Final import submit
  const handleImportSubmit = async () => {
    if (!importFile) return;
    if (!importSpecId) {
      setToastMessage(isRTL ? 'يرجى اختيار الموهبة' : 'Please select a specialization');
      setToastType('error');
      setToastVisible(true);
      return;
    }
    setImporting(true);
    try {
      const result = await talentTeachersApi.importExcel(importFile, importSpecId, importProjId, importActId);

      // Check for conflicts
      if (result.conflicts && result.conflicts.length > 0) {
        setImportResult(result);
        setImportConflicts(result.conflicts);
        const resMap: Record<number, any> = {};
        result.conflicts.forEach((c: any) => {
          resMap[c.row] = {
            selectedTeacherId: null,
            isNew: false,
            editedData: { name: c.row_data.name, eid: c.row_data.eid || '', email: c.row_data.email || '', phone: c.row_data.phone || '' },
            hasEdited: false,
          };
        });
        setConflictResolutions(resMap);
        setImportStep('conflicts');
        if (result.created > 0 || result.existing > 0) {
          setToastMessage(result.message);
          setToastType('info');
          setToastVisible(true);
        }
        await fetchTeachers();
      } else {
        // No conflicts
        setToastMessage(result.message || (isRTL ? `تم استيراد ${result.imported} منسق` : `Imported ${result.imported} teachers`));
        setToastType('success');
        setToastVisible(true);
        setShowImportModal(false);
        resetImportWizard();
        await fetchTeachers();
        setCurrentPage(1);
      }
    } catch (err: any) {
      const msg = err?.response?.data?.error || (isRTL ? 'خطأ في استيراد الملف' : 'Import error');
      setToastMessage(msg);
      setToastType('error');
      setToastVisible(true);
    } finally {
      setImporting(false);
    }
  };

  // Handle resolving conflicts
  const handleResolveConflicts = async () => {
    // Validate: must select a teacher AND change the conflicting field
    for (const [rowKey, res] of Object.entries(conflictResolutions)) {
      if (!res.selectedTeacherId && !res.isNew) {
        setToastMessage(isRTL ? 'يجب اختيار المنسق الصحيح أولاً' : 'You must select the correct teacher first');
        setToastType('error');
        setToastVisible(true);
        return;
      }
      // Find the conflict to check the conflicting field
      const conflict = importConflicts.find((c: any) => String(c.row) === rowKey);
      if (conflict) {
        const cf = conflict.conflict_field;
        const others = conflict.candidates.filter((c: any) =>
          res.isNew ? !c.is_new : c.id !== res.selectedTeacherId
        );
        for (const other of others) {
          if (cf === 'phone' && res.editedData.phone && other.phone && res.editedData.phone === other.phone) {
            setToastMessage(isRTL ? 'يجب تغيير رقم الهاتف لحل التعارض — الرقم الحالي مستخدم لمنسق آخر' : 'You must change the phone number to resolve the conflict');
            setToastType('error');
            setToastVisible(true);
            return;
          }
          if (cf === 'email' && res.editedData.email && other.email && res.editedData.email.toLowerCase() === other.email.toLowerCase()) {
            setToastMessage(isRTL ? 'يجب تغيير البريد الإلكتروني لحل التعارض — البريد الحالي مستخدم لمنسق آخر' : 'You must change the email to resolve the conflict');
            setToastType('error');
            setToastVisible(true);
            return;
          }
          if (cf === 'eid') {
            const phoneStillSame = res.editedData.phone && other.phone && res.editedData.phone === other.phone;
            const emailStillSame = res.editedData.email && other.email && res.editedData.email.toLowerCase() === other.email.toLowerCase();
            if (phoneStillSame && emailStillSame) {
              setToastMessage(isRTL ? 'يجب تغيير رقم الهاتف أو البريد الإلكتروني لحل التعارض' : 'You must change the phone or email to resolve the conflict');
              setToastType('error');
              setToastVisible(true);
              return;
            }
          }
        }
      }
    }
    setResolving(true);
    try {
      const resolutions = Object.entries(conflictResolutions).map(([rowKey, res]) => {
        const conflict = importConflicts.find((c: any) => String(c.row) === rowKey);
        return {
          teacher_id: res.selectedTeacherId,
          is_new: res.isNew,
          update_data: res.editedData,
          row_data: conflict?.row_data || {},
          specialization_id: importSpecId,
          project_id: importProjId,
          activity_id: importActId,
        };
      });
      const result = await talentTeachersApi.resolveConflicts(resolutions);
      setToastMessage(result.message || (isRTL ? 'تم حل التعارضات بنجاح' : 'Conflicts resolved'));
      setToastType('success');
      setToastVisible(true);
      setShowImportModal(false);
      resetImportWizard();
      await fetchTeachers();
      setCurrentPage(1);
    } catch (err: any) {
      const msg = err?.response?.data?.error || (isRTL ? 'خطأ في معالجة التعارضات' : 'Error resolving conflicts');
      setToastMessage(msg);
      setToastType('error');
      setToastVisible(true);
    } finally {
      setResolving(false);
    }
  };

  // Handle skipping conflicts
  const handleSkipConflicts = () => {
    setShowSkipConfirm(false);
    setShowImportModal(false);
    resetImportWizard();
    setToastMessage(isRTL ? 'تم تخطي المنسقين المتعارضين' : 'Conflicting teachers skipped');
    setToastType('info');
    setToastVisible(true);
  };

  const handleExportExcel = () => {
    talentTeachersApi.exportExcel();
  };

  const handleAddTeacher = async () => {
    if (!addForm.name.trim()) {
      setToastMessage('يرجى إدخال اسم المنسق');
      setToastType('error');
      setToastVisible(true);
      return;
    }
    setAddLoading(true);
    try {
      await talentTeachersApi.create({
        name: addForm.name,
        specialization: addForm.specialization,
        school: addForm.school,
        email: addForm.email,
        phone: addForm.phone,
      });
      setToastMessage('تم إضافة المنسق بنجاح');
      setToastType('success');
      setToastVisible(true);
      setShowAddModal(false);
      setAddForm({ name: '', specialization: '', school: '', email: '', phone: '' });
      await fetchTeachers();
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.response?.data?.detail || 'خطأ في إضافة المنسق';
      setToastMessage(msg);
      setToastType('error');
      setToastVisible(true);
    } finally {
      setAddLoading(false);
    }
  };

  const themeColors = {
    card: colors.card,
    cardBorder: colors.cardBorder,
    textPrimary: colors.textPrimary,
    textSecondary: colors.textMuted,
    tableHeader: isDark ? colors.backgroundAlt : '#F8FAFC',
    tableRow: colors.card,
    tableRowAlt: isDark ? colors.backgroundAlt : '#FAFAFA',
    border: colors.border,
  };

  const goToPage = (page: number) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  const getVisiblePages = (): (number | 'ellipsis')[] => {
    if (totalPages <= 5) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    if (currentPage <= 3) {
      return [1, 2, 3, 'ellipsis', totalPages];
    }
    if (currentPage >= totalPages - 2) {
      return [1, 'ellipsis', totalPages - 2, totalPages - 1, totalPages];
    }
    return [1, 'ellipsis', currentPage - 1, currentPage, currentPage + 1, 'ellipsis', totalPages];
  };

  // handleExportExcel is defined above with the other handlers

  const applyFilter = () => {
    setCurrentPage(1);
    setShowSchoolDropdown(false);
    setShowSpecDropdown(false);
  };

  const renderDropdown = (
    label: string,
    value: string,
    options: string[],
    isOpen: boolean,
    onToggle: () => void,
    onSelect: (val: string) => void,
    zIndex: number,
  ) => (
    <View style={[styles.filterDropdownContainer, { zIndex }]}>
      <TouchableOpacity
        style={[styles.filterDropdown, { backgroundColor: themeColors.card, borderColor: value !== 'all' ? '#a9211f' : themeColors.cardBorder }]}
        onPress={onToggle}
      >
        <ChevronDownIcon size={14} color={value !== 'all' ? '#a9211f' : themeColors.textSecondary} />
        <Text style={[styles.filterDropdownText, { color: value !== 'all' ? '#a9211f' : themeColors.textPrimary }]} numberOfLines={1}>
          {value === 'all' ? label : value}
        </Text>
      </TouchableOpacity>
      {isOpen && (
        <View style={[styles.dropdownMenu, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}>
          <ScrollView style={{ maxHeight: 200 }} nestedScrollEnabled>
            <TouchableOpacity
              style={[styles.dropdownItem, { borderBottomColor: themeColors.border }]}
              onPress={() => { onSelect('all'); onToggle(); }}
            >
              <View style={styles.dropdownItemRow}>
                {value === 'all' && <CheckIcon size={16} color="#a9211f" />}
                <Text style={[styles.dropdownItemText, { color: themeColors.textPrimary }, value === 'all' && styles.dropdownItemTextActive]}>
                  {label}
                </Text>
              </View>
            </TouchableOpacity>
            {options.map((opt, idx) => (
              <TouchableOpacity
                key={opt}
                style={[styles.dropdownItem, { borderBottomColor: themeColors.border }, idx === options.length - 1 && { borderBottomWidth: 0 }]}
                onPress={() => { onSelect(opt); onToggle(); }}
              >
                <View style={styles.dropdownItemRow}>
                  {value === opt && <CheckIcon size={16} color="#a9211f" />}
                  <Text style={[styles.dropdownItemText, { color: themeColors.textPrimary }, value === opt && styles.dropdownItemTextActive]} numberOfLines={1}>
                    {opt}
                  </Text>
                </View>
              </TouchableOpacity>
            ))}
          </ScrollView>
        </View>
      )}
    </View>
  );

  return (
    <View style={[styles.container, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }, createShadow(0, 1, 3, '#000', 0.1)]}>
      {/* Header */}
      <View style={[styles.headerRow, { flexDirection: rtlRow }]}>
        <Text style={[styles.sectionTitle, { color: themeColors.textPrimary, textAlign: isRTL ? 'right' : 'left' }]}>
          {t('talentsPage.teachers')}
        </Text>
        <View style={[styles.headerButtons, { flexDirection: rtlRow }]}>
            <TouchableOpacity
              style={[styles.secondaryButton, { borderColor: themeColors.cardBorder }]}
              onPress={handleExportExcel}
            >
              <Ionicons name="download-outline" size={16} color={themeColors.textSecondary} />
              <Text style={[styles.secondaryButtonText, { color: themeColors.textPrimary }]}>
                {t('talentsPage.exportExcel')}
              </Text>
            </TouchableOpacity>
            {!readOnly && (
              <TouchableOpacity
                style={[styles.secondaryButton, { borderColor: themeColors.cardBorder }]}
                onPress={() => setShowImportModal(true)}
              >
                <Ionicons name="cloud-upload-outline" size={16} color={themeColors.textSecondary} />
                <Text style={[styles.secondaryButtonText, { color: themeColors.textPrimary }]}>
                  {isRTL ? 'استيراد Excel' : 'Import Excel'}
                </Text>
              </TouchableOpacity>
            )}
            {!readOnly && (
              <TouchableOpacity
                style={{ flexDirection: 'row', alignItems: 'center', backgroundColor: '#a9211f', paddingHorizontal: 16, paddingVertical: 10, borderRadius: 8, gap: 8 }}
                onPress={() => navigation.navigate('EditTeacher', {})}
              >
                <Ionicons name="add" size={16} color="#FFFFFF" />
                <Text style={{ fontSize: 14, fontFamily: FONTS.medium, color: '#FFFFFF' }}>
                  {isRTL ? 'إضافة منسق' : 'Add Teacher'}
                </Text>
              </TouchableOpacity>
            )}
          </View>
      </View>

      {/* Filters */}
      <View style={[styles.filterRow, { flexDirection: rtlRow, zIndex: 100 }]}>
        {renderDropdown(
          t('talentsPage.allSpecializations'), filterSpecialization, UNIQUE_SPECIALIZATIONS,
          showSpecDropdown,
          () => { setShowSpecDropdown(!showSpecDropdown); setShowSchoolDropdown(false); },
          setFilterSpecialization, 20,
        )}
        {renderDropdown(
          t('talentsPage.allSchools'), filterSchool, UNIQUE_SCHOOLS,
          showSchoolDropdown,
          () => { setShowSchoolDropdown(!showSchoolDropdown); setShowSpecDropdown(false); },
          setFilterSchool, 10,
        )}
        <TouchableOpacity style={[styles.applyFilterBtn, { backgroundColor: isDark ? colors.backgroundAlt : '#F1F5F9' }]} onPress={applyFilter}>
          <Text style={[styles.applyFilterText, { color: themeColors.textPrimary }]}>
            {t('talentsPage.applyFilter')}
          </Text>
        </TouchableOpacity>
      </View>

      {/* Table */}
      <View style={[styles.tableContainer, { borderColor: themeColors.cardBorder }]}>
        <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ flexGrow: 1, minWidth: '100%' }}>
          <View style={{ minWidth: 1060, width: '100%' }}>
            {/* Header Row */}
            <View style={[styles.tableHeader, { backgroundColor: themeColors.tableHeader, borderBottomColor: themeColors.border, flexDirection: rtlRow }]}>
              <View style={[styles.tableCell, styles.nameCell]}>
                <TouchableOpacity onPress={toggleSort} style={[styles.sortableHeader, { flexDirection: rtlRow }]}>
                  <Ionicons name={sortOrder === 'asc' ? 'arrow-up' : sortOrder === 'desc' ? 'arrow-down' : 'swap-vertical'} size={14} color={sortOrder ? '#a9211f' : themeColors.textSecondary} />
                  <Text style={[styles.tableHeaderText, { color: themeColors.textPrimary }]}>{t('talentsPage.teacherName')}</Text>
                </TouchableOpacity>
              </View>
              <View style={[styles.tableCell, styles.specCell]}>
                <Text style={[styles.tableHeaderText, { color: themeColors.textPrimary }]}>{t('talentsPage.specialization')}</Text>
              </View>
              <View style={[styles.tableCell, styles.schoolCell]}>
                <Text style={[styles.tableHeaderText, { color: themeColors.textPrimary }]}>{t('talentsPage.school')}</Text>
              </View>
              <View style={[styles.tableCell, styles.emailCell]}>
                <Text style={[styles.tableHeaderText, { color: themeColors.textPrimary }]}>{t('talentsPage.email')}</Text>
              </View>
              <View style={[styles.tableCell, styles.phoneCell]}>
                <Text style={[styles.tableHeaderText, { color: themeColors.textPrimary }]}>{t('talentsPage.phone')}</Text>
              </View>
              <View style={[styles.tableCell, styles.actionCell]} />
              {!readOnly && <View style={[styles.tableCell, styles.actionCell]} />}
              {!readOnly && <View style={[styles.tableCell, styles.actionCell]} />}
            </View>

            {/* Data Rows */}
            {loading ? (
              <View style={styles.emptyRow}>
                <ActivityIndicator size="small" color="#a9211f" />
              </View>
            ) : data.results.length > 0 ? (
              data.results.map((teacher: Teacher, index: number) => (
                <View key={teacher.id} style={[styles.tableRow, { backgroundColor: themeColors.tableRow, borderBottomColor: isDark ? themeColors.border : '#EDF2F7', flexDirection: rtlRow }]}>
                  <View style={[styles.tableCell, styles.nameCell]}>
                    <Text style={[styles.cellText, { color: themeColors.textPrimary }]}>{teacher.name}</Text>
                  </View>
                  <View style={[styles.tableCell, styles.specCell]}>
                    <Text style={[styles.cellText, { color: themeColors.textPrimary }]}>{teacher.specialization}</Text>
                  </View>
                  <View style={[styles.tableCell, styles.schoolCell]}>
                    <Text style={[styles.cellText, { color: themeColors.textPrimary }]} numberOfLines={1}>{teacher.school}</Text>
                  </View>
                  <View style={[styles.tableCell, styles.emailCell]}>
                    <Text style={[styles.cellText, { color: themeColors.textPrimary }]} numberOfLines={1}>{teacher.email}</Text>
                  </View>
                  <View style={[styles.tableCell, styles.phoneCell]}>
                    <Text style={[styles.cellText, { color: themeColors.textPrimary }]}>{teacher.phone}</Text>
                  </View>
                  <View style={[styles.tableCell, styles.actionCell]}>
                    <TouchableOpacity onPress={() => navigation.navigate('TeacherDetail', { id: teacher.id })}>
                      <Ionicons name="eye-outline" size={18} color={themeColors.textSecondary} />
                    </TouchableOpacity>
                  </View>
                  {!readOnly && (
                    <View style={[styles.tableCell, styles.actionCell]}>
                      <TouchableOpacity onPress={() => navigation.navigate('EditTeacher', { id: teacher.id })}>
                        <Ionicons name="create-outline" size={18} color={themeColors.textSecondary} />
                      </TouchableOpacity>
                    </View>
                  )}
                  {!readOnly && (
                    <View style={[styles.tableCell, styles.actionCell]}>
                      <TouchableOpacity onPress={() => handleDeleteTeacher(teacher)}>
                        <Ionicons name="trash-outline" size={18} color="#d62728" />
                      </TouchableOpacity>
                    </View>
                  )}
                </View>
              ))
            ) : (
              <View style={styles.emptyRow}>
                <Text style={[styles.emptyText, { color: themeColors.textSecondary }]}>{t('common.noData')}</Text>
              </View>
            )}
          </View>
        </ScrollView>
      </View>

      {/* Pagination */}
      <View style={[styles.paginationContainer, { flexDirection: rtlRow }]}>
        <Text style={[styles.paginationInfo, { color: themeColors.textSecondary }]}>
          {`${t('talentsPage.showing')} ${Math.min((currentPage - 1) * pageSize + 1, data.count)}-${Math.min(currentPage * pageSize, data.count)} ${t('talentsPage.of')} ${data.count} ${t('talentsPage.items')}`}
        </Text>
        <View style={[styles.paginationControls, { flexDirection: rtlRow }]}>
          <TouchableOpacity
            style={[styles.paginationButton, currentPage === 1 && styles.paginationButtonDisabled]}
            onPress={() => goToPage(currentPage - 1)}
            disabled={currentPage === 1}
          >
            <Text style={[styles.paginationButtonText, { color: currentPage === 1 ? '#9CA3AF' : themeColors.textPrimary }]}>{t('talentsPage.previous')}</Text>
            <ChevronRightIcon size={16} color={currentPage === 1 ? '#9CA3AF' : themeColors.textPrimary} />
          </TouchableOpacity>
          <View style={[styles.pageNumbers, { flexDirection: rtlRow }]}>
            {getVisiblePages().map((item, idx) =>
              item === 'ellipsis' ? (
                <View key={`ellipsis-${idx}`} style={styles.pageNumber}>
                  <Text style={styles.pageNumberText}>...</Text>
                </View>
              ) : (
                <TouchableOpacity key={item} style={[styles.pageNumber, currentPage === item && styles.pageNumberActive]} onPress={() => goToPage(item)}>
                  <Text style={[styles.pageNumberText, currentPage === item && styles.pageNumberTextActive]}>{item}</Text>
                </TouchableOpacity>
              )
            )}
          </View>
          <TouchableOpacity
            style={[styles.paginationButton, currentPage === totalPages && styles.paginationButtonDisabled]}
            onPress={() => goToPage(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            <ChevronLeftIcon size={16} color={currentPage === totalPages ? '#9CA3AF' : themeColors.textPrimary} />
            <Text style={[styles.paginationButtonText, { color: currentPage === totalPages ? '#9CA3AF' : themeColors.textPrimary }]}>{t('talentsPage.next')}</Text>
          </TouchableOpacity>
        </View>
      </View>

      {/* Import Modal — 4-step wizard */}
      <Modal visible={showImportModal} transparent animationType="fade" onRequestClose={() => { setShowImportModal(false); resetImportWizard(); }}>
        <TouchableOpacity style={styles.modalOverlay} activeOpacity={1} onPress={() => { setShowImportModal(false); resetImportWizard(); }}>
          <TouchableOpacity activeOpacity={1} style={[styles.importContent, { backgroundColor: isDark ? colors.card : '#FFFFFF' }]}>
            {/* Header */}
            <View style={styles.importHeader}>
              <TouchableOpacity onPress={() => { setShowImportModal(false); resetImportWizard(); }}>
                <Ionicons name="close" size={22} color={themeColors.textPrimary} />
              </TouchableOpacity>
              <Text style={[styles.importTitle, { color: themeColors.textPrimary }]}>
                {isRTL ? 'استيراد قائمة المنسقين' : 'Import Teachers List'}
              </Text>
            </View>

            {importStep === 'pick' ? (
              <>
                {/* Step 1: Download Template */}
                <View style={[styles.importSection, { backgroundColor: isDark ? colors.backgroundAlt : '#F8FAFC', borderColor: isDark ? themeColors.cardBorder : '#E2E8F0' }]}>
                  <View style={styles.importStepRow}>
                    <Text style={[styles.importStepTitle, { color: themeColors.textPrimary }]}>{isRTL ? '١. تحميل القالب' : '1. Download Template'}</Text>
                    <View style={[styles.importStepBadge, { backgroundColor: '#A9211F' }]}><Text style={styles.importStepBadgeText}>1</Text></View>
                  </View>
                  <Text style={[styles.importStepDesc, { color: themeColors.textSecondary }]}>
                    {isRTL ? 'حمّل ملف Excel يحتوي على الأعمدة المطلوبة، ثم املأ بيانات المنسقين' : 'Download an Excel file with required columns, then fill in teacher data'}
                  </Text>
                  <TouchableOpacity style={[styles.importTemplateBtn, { borderColor: '#A9211F' }]} onPress={handleDownloadTemplate}>
                    <Ionicons name="download-outline" size={18} color="#A9211F" />
                    <Text style={[styles.importTemplateBtnText, { color: '#A9211F' }]}>{isRTL ? 'تحميل القالب' : 'Download Template'}</Text>
                  </TouchableOpacity>
                </View>

                {/* Step 2: Upload File */}
                <View style={[styles.importSection, { backgroundColor: isDark ? colors.backgroundAlt : '#F8FAFC', borderColor: isDark ? themeColors.cardBorder : '#E2E8F0' }]}>
                  <View style={styles.importStepRow}>
                    <Text style={[styles.importStepTitle, { color: themeColors.textPrimary }]}>{isRTL ? '٢. رفع الملف' : '2. Upload File'}</Text>
                    <View style={[styles.importStepBadge, { backgroundColor: '#A9211F' }]}><Text style={styles.importStepBadgeText}>2</Text></View>
                  </View>
                  <Text style={[styles.importStepDesc, { color: themeColors.textSecondary }]}>
                    {isRTL ? 'ارفع ملف Excel المعبأ بالبيانات لاستيراد المنسقين' : 'Upload the filled Excel file to import teachers'}
                  </Text>
                  <TouchableOpacity style={[styles.importUploadBtn, { backgroundColor: '#A9211F' }]} onPress={handlePickFile}>
                    <Ionicons name="cloud-upload-outline" size={18} color="#FFFFFF" />
                    <Text style={styles.importUploadBtnText}>{isRTL ? 'اختر ملف Excel' : 'Choose Excel File'}</Text>
                  </TouchableOpacity>
                </View>
              </>
            ) : importStep === 'assign' ? (
              <>
                {/* Step 3: Assignment Dropdowns */}
                <View style={[styles.importSection, { backgroundColor: isDark ? colors.backgroundAlt : '#F8FAFC', borderColor: isDark ? themeColors.cardBorder : '#E2E8F0' }]}>
                  <View style={styles.importStepRow}>
                    <Text style={[styles.importStepTitle, { color: themeColors.textPrimary }]}>
                      {isRTL ? '٣. تحديد الارتباطات' : '3. Assign To'}
                    </Text>
                    <View style={[styles.importStepBadge, { backgroundColor: '#A9211F' }]}><Text style={styles.importStepBadgeText}>3</Text></View>
                  </View>

                  {/* File name indicator */}
                  <View style={{ flexDirection: rtlRow, alignItems: 'center', gap: 8, marginBottom: 12, paddingVertical: 8, paddingHorizontal: 12, backgroundColor: isDark ? colors.backgroundAlt : '#EFF6FF', borderRadius: 8 }}>
                    <Ionicons name="document-outline" size={18} color="#3B82F6" />
                    <Text style={{ fontSize: 13, fontFamily: FONTS.regular, color: themeColors.textPrimary, flex: 1, textAlign: isRTL ? 'right' : 'left' }}>
                      {importFile?.name}
                    </Text>
                    <TouchableOpacity onPress={() => { setImportStep('pick'); setImportFile(null); }}>
                      <Text style={{ fontSize: 12, fontFamily: FONTS.medium, color: '#A9211F' }}>
                        {isRTL ? 'تغيير' : 'Change'}
                      </Text>
                    </TouchableOpacity>
                  </View>

                  {/* Specialization — freely selectable */}
                  <View style={{ marginBottom: 12 }}>
                    <Text style={{ fontSize: 13, fontFamily: FONTS.medium, color: themeColors.textPrimary, textAlign: isRTL ? 'right' : 'left', marginBottom: 6 }}>
                      {isRTL ? 'الموهبة' : 'Specialization'} <Text style={{ color: '#DC2626' }}>*</Text>
                    </Text>
                    <View style={{ borderWidth: 1, borderColor: isDark ? colors.border : '#E2E8F0', borderRadius: 8, backgroundColor: isDark ? colors.backgroundAlt : '#FFFFFF' }}>
                      <ScrollView horizontal={false} style={{ maxHeight: 200 }} nestedScrollEnabled>
                        {specOptions.map((spec) => (
                          <TouchableOpacity
                            key={spec.id}
                            style={{
                              flexDirection: rtlRow, alignItems: 'center', paddingVertical: 10, paddingHorizontal: 12,
                              borderBottomWidth: 1, borderBottomColor: isDark ? colors.border : '#F1F5F9',
                              backgroundColor: importSpecId === spec.id ? (isDark ? '#1E3A5F' : '#FEF2F2') : 'transparent',
                            }}
                            onPress={() => onImportSpecChange(spec.id)}
                          >
                            <View style={{
                              width: 18, height: 18, borderRadius: 9, borderWidth: 2,
                              borderColor: importSpecId === spec.id ? '#A9211F' : '#CBD5E1',
                              backgroundColor: importSpecId === spec.id ? '#A9211F' : 'transparent',
                              alignItems: 'center', justifyContent: 'center', marginEnd: 8,
                            }}>
                              {importSpecId === spec.id && <View style={{ width: 6, height: 6, borderRadius: 3, backgroundColor: '#fff' }} />}
                            </View>
                            <Text style={{ fontSize: 14, fontFamily: FONTS.regular, color: themeColors.textPrimary }}>{spec.name}</Text>
                          </TouchableOpacity>
                        ))}
                      </ScrollView>
                    </View>
                  </View>

                  {/* Project — freely selectable, shown when projects exist */}
                  {projOptions.length > 0 ? (
                    <View style={{ marginBottom: 12 }}>
                      <Text style={{ fontSize: 13, fontFamily: FONTS.medium, color: themeColors.textPrimary, textAlign: isRTL ? 'right' : 'left', marginBottom: 6 }}>
                        {isRTL ? 'المشروع (اختياري)' : 'Project (optional)'}
                      </Text>
                      <View style={{ borderWidth: 1, borderColor: isDark ? colors.border : '#E2E8F0', borderRadius: 8, backgroundColor: isDark ? colors.backgroundAlt : '#FFFFFF' }}>
                        <ScrollView horizontal={false} style={{ maxHeight: 160 }} nestedScrollEnabled>
                          <TouchableOpacity
                            style={{ flexDirection: rtlRow, alignItems: 'center', paddingVertical: 10, paddingHorizontal: 12, borderBottomWidth: 1, borderBottomColor: isDark ? colors.border : '#F1F5F9', backgroundColor: !importProjId ? (isDark ? '#1E3A5F' : '#F1F5F9') : 'transparent' }}
                            onPress={() => onImportProjChange(undefined)}
                          >
                            <View style={{ width: 18, height: 18, borderRadius: 9, borderWidth: 2, borderColor: !importProjId ? '#A9211F' : '#CBD5E1', backgroundColor: !importProjId ? '#A9211F' : 'transparent', alignItems: 'center', justifyContent: 'center', marginEnd: 8 }}>
                              {!importProjId && <View style={{ width: 6, height: 6, borderRadius: 3, backgroundColor: '#fff' }} />}
                            </View>
                            <Text style={{ fontSize: 14, fontFamily: FONTS.regular, color: themeColors.textSecondary }}>{isRTL ? 'بدون مشروع' : 'None'}</Text>
                          </TouchableOpacity>
                          {projOptions.map((proj) => (
                            <TouchableOpacity
                              key={proj.id}
                              style={{
                                flexDirection: rtlRow, alignItems: 'center', paddingVertical: 10, paddingHorizontal: 12,
                                borderBottomWidth: 1, borderBottomColor: isDark ? colors.border : '#F1F5F9',
                                backgroundColor: importProjId === proj.id ? (isDark ? '#1E3A5F' : '#FEF2F2') : 'transparent',
                              }}
                              onPress={() => onImportProjChange(proj.id)}
                            >
                              <View style={{ width: 18, height: 18, borderRadius: 9, borderWidth: 2, borderColor: importProjId === proj.id ? '#A9211F' : '#CBD5E1', backgroundColor: importProjId === proj.id ? '#A9211F' : 'transparent', alignItems: 'center', justifyContent: 'center', marginEnd: 8 }}>
                                {importProjId === proj.id && <View style={{ width: 6, height: 6, borderRadius: 3, backgroundColor: '#fff' }} />}
                              </View>
                              <Text style={{ fontSize: 14, fontFamily: FONTS.regular, color: themeColors.textPrimary }}>{proj.name}</Text>
                            </TouchableOpacity>
                          ))}
                        </ScrollView>
                      </View>
                    </View>
                  ) : null}

                  {/* Activity — freely selectable, shown when activities exist */}
                  {actOptions.length > 0 ? (
                    <View style={{ marginBottom: 12 }}>
                      <Text style={{ fontSize: 13, fontFamily: FONTS.medium, color: themeColors.textPrimary, textAlign: isRTL ? 'right' : 'left', marginBottom: 6 }}>
                        {isRTL ? 'النشاط (اختياري)' : 'Activity (optional)'}
                      </Text>
                      <View style={{ borderWidth: 1, borderColor: isDark ? colors.border : '#E2E8F0', borderRadius: 8, backgroundColor: isDark ? colors.backgroundAlt : '#FFFFFF' }}>
                        <ScrollView horizontal={false} style={{ maxHeight: 160 }} nestedScrollEnabled>
                          <TouchableOpacity
                            style={{ flexDirection: rtlRow, alignItems: 'center', paddingVertical: 10, paddingHorizontal: 12, borderBottomWidth: 1, borderBottomColor: isDark ? colors.border : '#F1F5F9', backgroundColor: !importActId ? (isDark ? '#1E3A5F' : '#F1F5F9') : 'transparent' }}
                            onPress={() => setImportActId(undefined)}
                          >
                            <View style={{ width: 18, height: 18, borderRadius: 9, borderWidth: 2, borderColor: !importActId ? '#A9211F' : '#CBD5E1', backgroundColor: !importActId ? '#A9211F' : 'transparent', alignItems: 'center', justifyContent: 'center', marginEnd: 8 }}>
                              {!importActId && <View style={{ width: 6, height: 6, borderRadius: 3, backgroundColor: '#fff' }} />}
                            </View>
                            <Text style={{ fontSize: 14, fontFamily: FONTS.regular, color: themeColors.textSecondary }}>{isRTL ? 'بدون نشاط' : 'None'}</Text>
                          </TouchableOpacity>
                          {actOptions.map((act) => (
                            <TouchableOpacity
                              key={act.id}
                              style={{
                                flexDirection: rtlRow, alignItems: 'center', paddingVertical: 10, paddingHorizontal: 12,
                                borderBottomWidth: 1, borderBottomColor: isDark ? colors.border : '#F1F5F9',
                                backgroundColor: importActId === act.id ? (isDark ? '#1E3A5F' : '#FEF2F2') : 'transparent',
                              }}
                              onPress={() => setImportActId(act.id)}
                            >
                              <View style={{ width: 18, height: 18, borderRadius: 9, borderWidth: 2, borderColor: importActId === act.id ? '#A9211F' : '#CBD5E1', backgroundColor: importActId === act.id ? '#A9211F' : 'transparent', alignItems: 'center', justifyContent: 'center', marginEnd: 8 }}>
                                {importActId === act.id && <View style={{ width: 6, height: 6, borderRadius: 3, backgroundColor: '#fff' }} />}
                              </View>
                              <Text style={{ fontSize: 14, fontFamily: FONTS.regular, color: themeColors.textPrimary }}>{act.name}</Text>
                            </TouchableOpacity>
                          ))}
                        </ScrollView>
                      </View>
                    </View>
                  ) : null}
                </View>

                {/* Action buttons */}
                <View style={{ flexDirection: 'row', gap: 12, marginTop: 8 }}>
                  <TouchableOpacity
                    style={{ flex: 1, paddingVertical: 12, borderRadius: 8, borderWidth: 1, borderColor: '#E2E8F0', alignItems: 'center' }}
                    onPress={() => { setImportStep('pick'); setImportFile(null); }}
                  >
                    <Text style={{ fontSize: 14, fontFamily: FONTS.medium, color: '#414042' }}>{isRTL ? 'رجوع' : 'Back'}</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={{ flex: 1, paddingVertical: 12, borderRadius: 8, backgroundColor: '#A9211F', alignItems: 'center', opacity: !importSpecId || importing ? 0.5 : 1 }}
                    onPress={handleImportSubmit}
                    disabled={!importSpecId || importing}
                  >
                    {importing ? (
                      <ActivityIndicator size="small" color="#fff" />
                    ) : (
                      <Text style={{ fontSize: 14, fontFamily: FONTS.medium, color: '#FFFFFF' }}>{isRTL ? 'استيراد' : 'Import'}</Text>
                    )}
                  </TouchableOpacity>
                </View>
              </>
            ) : importStep === 'conflicts' ? (
              <>
                {/* Conflicts Warning Screen */}
                <View style={[styles.importSection, { backgroundColor: isDark ? colors.backgroundAlt : '#FFF7ED', borderColor: '#F59E0B' }]}>
                  <View style={{ alignItems: 'center', marginBottom: 12 }}>
                    <Ionicons name="warning-outline" size={48} color="#F59E0B" />
                  </View>
                  <Text style={{ fontSize: 16, fontFamily: FONTS.bold, color: themeColors.textPrimary, textAlign: 'center', marginBottom: 8 }}>
                    {isRTL
                      ? `وجدنا ${importConflicts.length} منسق لديهم نفس الرقم أو البريد الإلكتروني`
                      : `Found ${importConflicts.length} teachers with duplicate phone or email`}
                  </Text>
                  {importResult && (importResult.created > 0 || importResult.existing > 0) && (
                    <Text style={{ fontSize: 13, fontFamily: FONTS.regular, color: themeColors.textSecondary, textAlign: 'center', marginBottom: 12 }}>
                      {importResult.message}
                    </Text>
                  )}
                  {importConflicts.map((c: any, idx: number) => (
                    <View key={idx} style={{ flexDirection: rtlRow, alignItems: 'center', paddingVertical: 8, paddingHorizontal: 12, backgroundColor: isDark ? colors.backgroundAlt : '#FFFFFF', borderRadius: 8, marginBottom: 6, borderWidth: 1, borderColor: isDark ? colors.border : '#FDE68A' }}>
                      <View style={{ width: 24, height: 24, borderRadius: 12, backgroundColor: '#F59E0B', alignItems: 'center', justifyContent: 'center', marginEnd: 10 }}>
                        <Text style={{ fontSize: 12, fontFamily: FONTS.bold, color: '#FFFFFF' }}>{idx + 1}</Text>
                      </View>
                      <View style={{ flex: 1 }}>
                        <Text style={{ fontSize: 14, fontFamily: FONTS.medium, color: themeColors.textPrimary, textAlign: isRTL ? 'right' : 'left' }}>{c.row_data.name}</Text>
                        <Text style={{ fontSize: 12, fontFamily: FONTS.regular, color: themeColors.textSecondary, textAlign: isRTL ? 'right' : 'left' }}>
                          {c.conflict_field === 'email'
                            ? (isRTL ? 'البريد الإلكتروني مختلف عن المنسق المسجل' : 'Email differs from registered teacher')
                            : c.conflict_field === 'phone'
                            ? (isRTL ? 'رقم الهاتف مختلف عن المنسق المسجل' : 'Phone differs from registered teacher')
                            : (isRTL ? 'نفس رقم الهوية — يجب تعديل الهاتف أو البريد' : 'Same EID — change phone or email')}
                        </Text>
                      </View>
                    </View>
                  ))}
                </View>

                {showSkipConfirm ? (
                  <View style={[styles.importSection, { backgroundColor: isDark ? colors.backgroundAlt : '#FEF2F2', borderColor: '#DC2626' }]}>
                    <Text style={{ fontSize: 14, fontFamily: FONTS.medium, color: themeColors.textPrimary, textAlign: 'center', marginBottom: 12 }}>
                      {isRTL ? 'هل أنت متأكد؟ لن يتم رفع هؤلاء المنسقين' : 'Are you sure? These teachers will not be uploaded'}
                    </Text>
                    <View style={{ flexDirection: 'row', gap: 12 }}>
                      <TouchableOpacity style={{ flex: 1, paddingVertical: 10, borderRadius: 8, borderWidth: 1, borderColor: '#E2E8F0', alignItems: 'center' }} onPress={() => setShowSkipConfirm(false)}>
                        <Text style={{ fontSize: 14, fontFamily: FONTS.medium, color: '#414042' }}>{isRTL ? 'إلغاء' : 'Cancel'}</Text>
                      </TouchableOpacity>
                      <TouchableOpacity style={{ flex: 1, paddingVertical: 10, borderRadius: 8, backgroundColor: '#DC2626', alignItems: 'center' }} onPress={handleSkipConflicts}>
                        <Text style={{ fontSize: 14, fontFamily: FONTS.medium, color: '#FFFFFF' }}>{isRTL ? 'تأكيد التخطي' : 'Confirm Skip'}</Text>
                      </TouchableOpacity>
                    </View>
                  </View>
                ) : (
                  <View style={{ flexDirection: 'row', gap: 12, marginTop: 8 }}>
                    <TouchableOpacity style={{ flex: 1, paddingVertical: 12, borderRadius: 8, borderWidth: 1, borderColor: '#E2E8F0', alignItems: 'center' }} onPress={() => setShowSkipConfirm(true)}>
                      <Text style={{ fontSize: 14, fontFamily: FONTS.medium, color: '#414042' }}>{isRTL ? 'تخطي' : 'Skip'}</Text>
                    </TouchableOpacity>
                    <TouchableOpacity style={{ flex: 1, paddingVertical: 12, borderRadius: 8, backgroundColor: '#A9211F', alignItems: 'center' }} onPress={() => setImportStep('resolve')}>
                      <Text style={{ fontSize: 14, fontFamily: FONTS.medium, color: '#FFFFFF' }}>{isRTL ? 'تعديل واختيار' : 'Edit & Select'}</Text>
                    </TouchableOpacity>
                  </View>
                )}
              </>
            ) : importStep === 'resolve' ? (
              <>
                {/* Conflict Resolution Screen */}
                <View style={styles.importStepRow}>
                  <Text style={[styles.importStepTitle, { color: themeColors.textPrimary, fontSize: 17 }]}>
                    {isRTL ? 'حل التعارضات' : 'Resolve Conflicts'}
                  </Text>
                  <View style={[styles.importStepBadge, { backgroundColor: '#F59E0B', width: 30, height: 30, borderRadius: 15 }]}>
                    <Text style={[styles.importStepBadgeText, { fontSize: 14 }]}>{importConflicts.length}</Text>
                  </View>
                </View>

                <ScrollView style={{ flexShrink: 1 }} nestedScrollEnabled>
                  {importConflicts.map((conflict: any, cIdx: number) => {
                    const res = conflictResolutions[conflict.row];
                    if (!res) return null;
                    return (
                      <View key={cIdx} style={{ marginBottom: 20, padding: 16, backgroundColor: isDark ? colors.backgroundAlt : '#FFFBEB', borderRadius: 12, borderWidth: 1, borderColor: isDark ? colors.border : '#FDE68A' }}>
                        {/* Conflict header */}
                        <View style={{ flexDirection: rtlRow, alignItems: 'center', marginBottom: 14, paddingBottom: 12, borderBottomWidth: 1, borderBottomColor: isDark ? colors.border : '#FDE68A' }}>
                          <View style={{ width: 32, height: 32, borderRadius: 16, backgroundColor: '#F59E0B', alignItems: 'center', justifyContent: 'center', marginEnd: 12 }}>
                            <Text style={{ fontSize: 15, fontFamily: FONTS.bold, color: '#FFFFFF' }}>{cIdx + 1}</Text>
                          </View>
                          <View style={{ flex: 1 }}>
                            <Text style={{ fontSize: 16, fontFamily: FONTS.bold, color: themeColors.textPrimary, textAlign: isRTL ? 'right' : 'left' }}>{conflict.row_data.name}</Text>
                            {conflict.row_data.eid ? (
                              <View style={{ flexDirection: rtlRow, alignItems: 'center', gap: 4, marginTop: 2 }}>
                                <Text style={{ fontSize: 13, fontFamily: FONTS.regular, color: themeColors.textSecondary }}>{isRTL ? 'رقم الهوية:' : 'EID:'}</Text>
                                <Text style={{ fontSize: 13, fontFamily: FONTS.regular, color: themeColors.textSecondary, writingDirection: 'ltr', direction: 'ltr' }}>{conflict.row_data.eid}</Text>
                              </View>
                            ) : null}
                            <Text style={{ fontSize: 13, fontFamily: FONTS.regular, color: '#F59E0B', textAlign: isRTL ? 'right' : 'left', marginTop: 4 }}>
                              {conflict.conflict_field === 'email'
                                ? (isRTL ? 'البريد الإلكتروني مختلف عن المنسق المسجل' : 'Email differs from registered teacher')
                                : conflict.conflict_field === 'phone'
                                ? (isRTL ? 'رقم الهاتف مختلف عن المنسق المسجل' : 'Phone differs from registered teacher')
                                : (isRTL ? 'نفس رقم الهوية — يجب تعديل الهاتف أو البريد' : 'Same EID — change phone or email')}
                            </Text>
                          </View>
                        </View>

                        {/* Candidate selection */}
                        <Text style={{ fontSize: 15, fontFamily: FONTS.bold, color: themeColors.textPrimary, textAlign: isRTL ? 'right' : 'left', marginBottom: 8 }}>
                          {isRTL ? 'اختر المنسق الصحيح:' : 'Select correct teacher:'}
                        </Text>
                        {conflict.candidates.map((cand: any, candIdx: number) => {
                          const candKey = cand.id ?? `new-${candIdx}`;
                          const isSelected = cand.is_new ? res.isNew : (res.selectedTeacherId === cand.id && !res.isNew);
                          return (
                          <TouchableOpacity
                            key={candKey}
                            style={{
                              flexDirection: rtlRow, alignItems: 'center', paddingVertical: 14, paddingHorizontal: 14,
                              borderWidth: 2, borderRadius: 10, marginBottom: 8,
                              borderColor: isSelected ? '#A9211F' : (isDark ? colors.border : '#E2E8F0'),
                              backgroundColor: isSelected ? (isDark ? '#1E3A5F' : '#FEF2F2') : (isDark ? colors.backgroundAlt : '#FFFFFF'),
                            }}
                            onPress={() => {
                              setConflictResolutions(prev => ({
                                ...prev,
                                [conflict.row]: {
                                  ...prev[conflict.row],
                                  selectedTeacherId: cand.id,
                                  isNew: !!cand.is_new,
                                  editedData: { name: cand.name, eid: cand.eid || '', email: cand.email || '', phone: cand.phone || '' },
                                  hasEdited: true,
                                },
                              }));
                            }}
                          >
                            <View style={{
                              width: 22, height: 22, borderRadius: 11, borderWidth: 2, marginEnd: 12,
                              borderColor: isSelected ? '#A9211F' : '#CBD5E1',
                              backgroundColor: isSelected ? '#A9211F' : 'transparent',
                              alignItems: 'center', justifyContent: 'center',
                            }}>
                              {isSelected && <View style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: '#fff' }} />}
                            </View>
                            <View style={{ flex: 1 }}>
                              <View style={{ flexDirection: rtlRow, alignItems: 'center', gap: 8 }}>
                                <Text style={{ fontSize: 15, fontFamily: FONTS.bold, color: themeColors.textPrimary, textAlign: isRTL ? 'right' : 'left' }}>{cand.name}</Text>
                                {cand.is_new && (
                                  <View style={{ backgroundColor: '#22C55E', paddingHorizontal: 8, paddingVertical: 2, borderRadius: 4 }}>
                                    <Text style={{ fontSize: 11, fontFamily: FONTS.bold, color: '#FFFFFF' }}>{isRTL ? 'جديد' : 'New'}</Text>
                                  </View>
                                )}
                              </View>
                              {cand.eid ? (
                                <View style={{ flexDirection: rtlRow, alignItems: 'center', gap: 4, marginTop: 2 }}>
                                  <Text style={{ fontSize: 13, fontFamily: FONTS.medium, color: themeColors.textSecondary }}>{isRTL ? 'الهوية:' : 'EID:'}</Text>
                                  <Text style={{ fontSize: 13, fontFamily: FONTS.medium, color: themeColors.textSecondary, writingDirection: 'ltr', direction: 'ltr' }}>{cand.eid}</Text>
                                </View>
                              ) : null}
                              <View style={{ flexDirection: rtlRow, alignItems: 'center', gap: 6, marginTop: 3, flexWrap: 'wrap' }}>
                                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                                  <Ionicons name="mail-outline" size={13} color={themeColors.textSecondary} />
                                  <Text style={{ fontSize: 13, fontFamily: FONTS.regular, color: themeColors.textSecondary, writingDirection: 'ltr', direction: 'ltr' }}>{cand.email || '-'}</Text>
                                </View>
                                <Text style={{ color: themeColors.textSecondary }}>|</Text>
                                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                                  <Ionicons name="call-outline" size={13} color={themeColors.textSecondary} />
                                  <Text style={{ fontSize: 13, fontFamily: FONTS.regular, color: themeColors.textSecondary, writingDirection: 'ltr', direction: 'ltr' }}>{cand.phone || '-'}</Text>
                                </View>
                              </View>
                            </View>
                          </TouchableOpacity>
                          );
                        })}

                        {/* Editable fields */}
                        <Text style={{ fontSize: 15, fontFamily: FONTS.bold, color: themeColors.textPrimary, textAlign: isRTL ? 'right' : 'left', marginTop: 14, marginBottom: 8 }}>
                          {isRTL ? 'تعديل البيانات:' : 'Edit details:'}
                        </Text>
                        {/* Name — read only */}
                        <View style={{ marginBottom: 8 }}>
                          <Text style={{ fontSize: 13, fontFamily: FONTS.medium, color: themeColors.textSecondary, textAlign: isRTL ? 'right' : 'left', marginBottom: 4 }}>{isRTL ? 'الاسم' : 'Name'}</Text>
                          <View style={{ borderWidth: 1, borderColor: isDark ? colors.border : '#E2E8F0', borderRadius: 8, paddingVertical: 10, paddingHorizontal: 12, backgroundColor: isDark ? colors.backgroundAlt : '#F8FAFC' }}>
                            <Text style={{ fontSize: 14, fontFamily: FONTS.regular, color: themeColors.textSecondary, textAlign: isRTL ? 'right' : 'left' }}>{res.editedData.name}</Text>
                          </View>
                        </View>
                        {/* EID — read only */}
                        <View style={{ marginBottom: 8 }}>
                          <Text style={{ fontSize: 13, fontFamily: FONTS.medium, color: themeColors.textSecondary, textAlign: isRTL ? 'right' : 'left', marginBottom: 4 }}>{isRTL ? 'رقم الهوية' : 'EID'}</Text>
                          <View style={{ borderWidth: 1, borderColor: isDark ? colors.border : '#E2E8F0', borderRadius: 8, paddingVertical: 10, paddingHorizontal: 12, backgroundColor: isDark ? colors.backgroundAlt : '#F8FAFC' }}>
                            <Text style={{ fontSize: 14, fontFamily: FONTS.regular, color: themeColors.textSecondary, writingDirection: 'ltr', direction: 'ltr', textAlign: 'left' }}>{res.editedData.eid || '-'}</Text>
                          </View>
                        </View>
                        {/* Email — editable */}
                        <View style={{ marginBottom: 8 }}>
                          <Text style={{ fontSize: 13, fontFamily: FONTS.medium, color: themeColors.textPrimary, textAlign: isRTL ? 'right' : 'left', marginBottom: 4 }}>{isRTL ? 'البريد الإلكتروني' : 'Email'}</Text>
                          <TextInput
                            style={{
                              borderWidth: 1, borderColor: isDark ? colors.border : '#CBD5E1', borderRadius: 8,
                              paddingVertical: 10, paddingHorizontal: 12, fontSize: 14, fontFamily: FONTS.regular,
                              color: themeColors.textPrimary, writingDirection: 'ltr', direction: 'ltr', textAlign: 'left',
                              backgroundColor: isDark ? colors.backgroundAlt : '#FFFFFF',
                            }}
                            value={res.editedData.email}
                            onChangeText={(val) => {
                              setConflictResolutions(prev => ({
                                ...prev,
                                [conflict.row]: {
                                  ...prev[conflict.row],
                                  editedData: { ...prev[conflict.row].editedData, email: val },
                                  hasEdited: true,
                                },
                              }));
                            }}
                            keyboardType="email-address"
                            autoCapitalize="none"
                          />
                        </View>
                        {/* Phone — editable */}
                        <View style={{ marginBottom: 4 }}>
                          <Text style={{ fontSize: 13, fontFamily: FONTS.medium, color: themeColors.textPrimary, textAlign: isRTL ? 'right' : 'left', marginBottom: 4 }}>{isRTL ? 'الهاتف' : 'Phone'}</Text>
                          <TextInput
                            style={{
                              borderWidth: 1, borderColor: isDark ? colors.border : '#CBD5E1', borderRadius: 8,
                              paddingVertical: 10, paddingHorizontal: 12, fontSize: 14, fontFamily: FONTS.regular,
                              color: themeColors.textPrimary, writingDirection: 'ltr', direction: 'ltr', textAlign: 'left',
                              backgroundColor: isDark ? colors.backgroundAlt : '#FFFFFF',
                            }}
                            value={res.editedData.phone}
                            onChangeText={(val) => {
                              setConflictResolutions(prev => ({
                                ...prev,
                                [conflict.row]: {
                                  ...prev[conflict.row],
                                  editedData: { ...prev[conflict.row].editedData, phone: val },
                                  hasEdited: true,
                                },
                              }));
                            }}
                            keyboardType="phone-pad"
                          />
                        </View>
                      </View>
                    );
                  })}
                </ScrollView>

                {/* Action buttons */}
                <View style={{ flexDirection: 'row', gap: 12, marginTop: 12 }}>
                  <TouchableOpacity style={{ flex: 1, paddingVertical: 14, borderRadius: 8, borderWidth: 1, borderColor: '#E2E8F0', alignItems: 'center' }} onPress={() => setImportStep('conflicts')}>
                    <Text style={{ fontSize: 15, fontFamily: FONTS.medium, color: '#414042' }}>{isRTL ? 'رجوع' : 'Back'}</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={{ flex: 1, paddingVertical: 14, borderRadius: 8, backgroundColor: '#A9211F', alignItems: 'center', opacity: resolving ? 0.5 : 1 }}
                    onPress={handleResolveConflicts}
                    disabled={resolving}
                  >
                    {resolving ? (
                      <ActivityIndicator size="small" color="#fff" />
                    ) : (
                      <Text style={{ fontSize: 15, fontFamily: FONTS.medium, color: '#FFFFFF' }}>{isRTL ? 'حفظ التعديلات' : 'Save Changes'}</Text>
                    )}
                  </TouchableOpacity>
                </View>
              </>
            ) : null}
          </TouchableOpacity>
        </TouchableOpacity>
      </Modal>

      {/* Hidden file input for Excel import */}
      {Platform.OS === 'web' && (
        <input
          ref={fileInputRef as any}
          type="file"
          accept=".xlsx,.xls"
          style={{ display: 'none' }}
          onChange={onFileSelected}
        />
      )}

      {/* Add Teacher Modal */}
      <Modal visible={showAddModal} transparent animationType="fade" onRequestClose={() => setShowAddModal(false)}>
        <TouchableOpacity style={styles.modalOverlay} activeOpacity={1} onPress={() => setShowAddModal(false)}>
          <TouchableOpacity activeOpacity={1} onPress={() => {}}>
            <View style={[styles.modalContent, { backgroundColor: colors.card }]}>
              <View style={[styles.modalHeader, { flexDirection: rtlRow }]}>
                <Text style={[styles.modalTitle, { color: colors.textPrimary }]}>إضافة منسق جديد</Text>
                <TouchableOpacity onPress={() => setShowAddModal(false)}>
                  <Ionicons name="close" size={22} color={colors.textSecondary} />
                </TouchableOpacity>
              </View>
              <View style={styles.modalBody}>
                {[
                  { key: 'name' as const, label: 'اسم المنسق', placeholder: 'أدخل اسم المنسق' },
                  { key: 'specialization' as const, label: 'الموهبة', placeholder: 'أدخل الموهبة' },
                  { key: 'school' as const, label: 'المدرسة', placeholder: 'أدخل اسم المدرسة' },
                  { key: 'email' as const, label: 'البريد الالكتروني', placeholder: 'أدخل البريد الالكتروني' },
                  { key: 'phone' as const, label: 'الهاتف', placeholder: 'أدخل رقم الهاتف' },
                ].map((field) => (
                  <View key={field.key} style={styles.formField}>
                    <Text style={[styles.formLabel, { color: colors.textSecondary, textAlign: isRTL ? 'right' : 'left' }]}>{field.label}</Text>
                    <TextInput
                      style={[styles.formInput, { color: colors.textPrimary, borderColor: colors.border, backgroundColor: isDark ? colors.backgroundAlt : '#F9FAFB', textAlign: isRTL ? 'right' : 'left' }]}
                      placeholder={field.placeholder}
                      placeholderTextColor={colors.textMuted}
                      value={addForm[field.key]}
                      onChangeText={(text) => setAddForm(prev => ({ ...prev, [field.key]: text }))}
                    />
                  </View>
                ))}
                <TouchableOpacity
                  style={[styles.submitButton, addLoading && { opacity: 0.6 }]}
                  onPress={handleAddTeacher}
                  disabled={addLoading}
                >
                  <Text style={styles.submitButtonText}>{addLoading ? 'جاري الإضافة...' : 'إضافة المنسق'}</Text>
                </TouchableOpacity>
              </View>
            </View>
          </TouchableOpacity>
        </TouchableOpacity>
      </Modal>

      {/* View Teacher Modal */}
      <Modal visible={!!viewTeacher} transparent animationType="fade" onRequestClose={() => setViewTeacher(null)}>
        <TouchableOpacity style={styles.modalOverlay} activeOpacity={1} onPress={() => setViewTeacher(null)}>
          <TouchableOpacity activeOpacity={1} onPress={() => {}}>
            <View style={[styles.viewModalContent, { backgroundColor: colors.card }]}>
              <View style={[styles.modalHeader, { flexDirection: rtlRow }]}>
                <Text style={[styles.modalTitle, { color: colors.textPrimary }]}>
                  {isRTL ? 'بيانات المنسق' : 'Teacher Details'}
                </Text>
                <TouchableOpacity onPress={() => setViewTeacher(null)}>
                  <Ionicons name="close" size={22} color={colors.textSecondary} />
                </TouchableOpacity>
              </View>
              {viewTeacher && (
                <View style={styles.viewModalBody}>
                  {[
                    { label: isRTL ? 'اسم المنسق' : 'Name', value: viewTeacher.name },
                    { label: isRTL ? 'اختصاص التنسيق' : 'Teaching Specialization', value: viewTeacher.teachingSpecialization },
                    { label: isRTL ? 'الموهبة' : 'Specialization', value: viewTeacher.specialization },
                    { label: isRTL ? 'المدرسة' : 'School', value: viewTeacher.school },
                    { label: isRTL ? 'المشروع' : 'Project', value: viewTeacher.projectNames.join('، ') },
                    { label: isRTL ? 'البريد الالكتروني' : 'Email', value: viewTeacher.email },
                    { label: isRTL ? 'الهاتف' : 'Phone', value: viewTeacher.phone },
                  ].map((item) => (
                    <View key={item.label} style={[styles.viewRow, { flexDirection: rtlRow }]}>
                      <Text style={[styles.viewLabel, { color: colors.textMuted }]}>{item.label}</Text>
                      <Text style={[styles.viewValue, { color: colors.textPrimary }]}>{item.value || '—'}</Text>
                    </View>
                  ))}
                </View>
              )}
            </View>
          </TouchableOpacity>
        </TouchableOpacity>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal visible={!!deleteTarget} transparent animationType="fade" onRequestClose={() => setDeleteTarget(null)}>
        <View style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'center', alignItems: 'center' }}>
          <View style={{ backgroundColor: colors.card, borderRadius: 12, padding: 24, width: 480, maxWidth: '90%', alignItems: 'center' }}>
            <Ionicons name="warning-outline" size={40} color="#EAB308" style={{ marginBottom: 12 }} />
            <Text style={{ fontSize: 18, fontFamily: FONTS.medium, color: colors.textPrimary, marginBottom: 8 }}>
              {isRTL ? 'إزالة المنسق' : 'Remove Teacher'}
            </Text>
            <Text style={{ fontSize: 14, fontFamily: FONTS.regular, color: colors.textSecondary, textAlign: 'center', lineHeight: 22, marginBottom: 20 }}>
              {isRTL ? `هل أنت متأكد من إزالة المنسق "${deleteTarget?.name}"؟` : `Are you sure you want to remove teacher "${deleteTarget?.name}"?`}
            </Text>
            <View style={{ flexDirection: 'row', gap: 12, width: '100%' }}>
              <TouchableOpacity
                style={{ flex: 1, paddingVertical: 12, borderRadius: 8, borderWidth: 1, borderColor: '#E2E8F0', alignItems: 'center' }}
                onPress={() => setDeleteTarget(null)}
              >
                <Text style={{ fontSize: 14, fontFamily: FONTS.medium, color: '#414042' }}>{isRTL ? 'إلغاء' : 'Cancel'}</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={{ flex: 1, paddingVertical: 12, borderRadius: 8, backgroundColor: '#DC2626', alignItems: 'center' }}
                onPress={confirmDeleteTeacher}
              >
                <Text style={{ fontSize: 14, fontFamily: FONTS.medium, color: '#FFFFFF' }}>{isRTL ? 'حذف' : 'Delete'}</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      <Toast visible={toastVisible} message={toastMessage} type={toastType} onHide={() => setToastVisible(false)} />
    </View>
  );
}

const styles = StyleSheet.create({
  container: { borderRadius: 12, borderWidth: 1, padding: 16, gap: 16 },
  headerRow: { justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 },
  sectionTitle: { fontSize: 20, fontFamily: FONTS.medium },
  headerButtons: { gap: 8, alignItems: 'center' },
  secondaryButton: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 16, paddingVertical: 10, borderRadius: 8, borderWidth: 1, gap: 8 },
  secondaryButtonText: { fontSize: 14, fontFamily: FONTS.regular },
  filterRow: { alignItems: 'center', gap: 12, position: 'relative' },
  applyFilterBtn: { paddingHorizontal: 16, paddingVertical: 8, borderRadius: 8 },
  applyFilterText: { fontSize: 14, fontFamily: FONTS.regular },
  filterDropdownContainer: { flex: 1, position: 'relative' },
  filterDropdown: { flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-end', gap: 6, paddingHorizontal: 14, paddingVertical: 10, borderRadius: 8, borderWidth: 1 },
  filterDropdownText: { fontSize: 14, fontFamily: FONTS.regular, textAlign: 'right' },
  dropdownMenu: { position: 'absolute', top: 44, left: 0, right: 0, borderRadius: 8, borderWidth: 1, shadowColor: '#000', shadowOffset: { width: 0, height: 4 }, shadowOpacity: 0.15, shadowRadius: 12, elevation: 10, zIndex: 9999, overflow: 'hidden' },
  dropdownItem: { paddingHorizontal: 12, paddingVertical: 12, borderBottomWidth: 1 },
  dropdownItemRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-end', gap: 8 },
  dropdownItemText: { fontSize: 14, fontFamily: FONTS.regular, textAlign: 'right' },
  dropdownItemTextActive: { color: '#a9211f', fontFamily: FONTS.medium },
  tableContainer: { borderRadius: 8, borderWidth: 1, overflow: 'hidden' },
  tableHeader: { flexDirection: 'row', paddingVertical: 12, paddingHorizontal: 16, borderBottomWidth: 1 },
  tableHeaderText: { fontSize: 14, fontFamily: FONTS.medium, textAlign: 'right' },
  sortableHeader: { alignItems: 'center', gap: 4 },
  tableRow: { flexDirection: 'row', paddingVertical: 12, paddingHorizontal: 16, borderBottomWidth: 1 },
  tableCell: { justifyContent: 'center', alignItems: 'flex-end', paddingHorizontal: 8 },
  nameCell: { flex: 1, minWidth: 150 },
  specCell: { flex: 1, minWidth: 140 },
  schoolCell: { flex: 1, minWidth: 160 },
  projectCell: { flex: 1, minWidth: 160 },
  emailCell: { flex: 1, minWidth: 180 },
  phoneCell: { width: 120 },
  actionCell: { width: 44, alignItems: 'center' },
  cellText: { fontSize: 14, fontFamily: FONTS.regular, textAlign: 'right' },
  emptyRow: { padding: 40, alignItems: 'center' },
  emptyText: { fontSize: 14, fontFamily: FONTS.regular },
  paginationContainer: { justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 },
  paginationControls: { alignItems: 'center', gap: 8 },
  paginationButton: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 12, paddingVertical: 8, gap: 4 },
  paginationButtonDisabled: { opacity: 0.5 },
  paginationButtonText: { fontSize: 14, fontFamily: FONTS.regular },
  pageNumbers: { alignItems: 'center', gap: 4 },
  pageNumber: { width: 32, height: 32, borderRadius: 8, justifyContent: 'center', alignItems: 'center' },
  pageNumberActive: { backgroundColor: '#F1F5F9' },
  pageNumberText: { fontSize: 14, fontFamily: FONTS.regular, color: '#6B7280' },
  pageNumberTextActive: { color: '#0F172A', fontFamily: FONTS.medium },
  paginationInfo: { fontSize: 14, fontFamily: FONTS.regular },
  modalOverlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'center', alignItems: 'center', padding: 20 },
  modalContent: { width: '100%', maxWidth: 500, borderRadius: 12, overflow: 'hidden' },
  modalHeader: { justifyContent: 'space-between', alignItems: 'center', padding: 16, borderBottomWidth: 1, borderBottomColor: '#E5E7EB' },
  modalTitle: { fontSize: 18, fontFamily: FONTS.medium },
  modalBody: { padding: 0 },
  modalRow: { justifyContent: 'space-between', paddingHorizontal: 16, paddingVertical: 12 },
  modalLabel: { fontSize: 13, fontFamily: FONTS.regular, flex: 1 },
  modalValue: { fontSize: 14, fontFamily: FONTS.medium, flex: 1 },
  formField: { paddingHorizontal: 16, paddingVertical: 8 },
  formLabel: { fontSize: 13, fontFamily: FONTS.medium, marginBottom: 6 },
  formInput: { borderWidth: 1, borderRadius: 8, paddingHorizontal: 12, paddingVertical: 10, fontSize: 14, fontFamily: FONTS.regular },
  submitButton: { backgroundColor: '#a9211f', marginHorizontal: 16, marginVertical: 12, paddingVertical: 12, borderRadius: 8, alignItems: 'center' },
  submitButtonText: { color: '#FFFFFF', fontSize: 14, fontFamily: FONTS.medium },
  importContent: { width: '96%', maxWidth: 780, borderRadius: 12, padding: 24, gap: 16, maxHeight: '98%' },
  importHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  importTitle: { fontSize: 18, fontFamily: FONTS.medium, textAlign: 'right' },
  importSection: { borderRadius: 8, borderWidth: 1, padding: 16, gap: 12 },
  importStepRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-end', gap: 10 },
  importStepBadge: { width: 26, height: 26, borderRadius: 13, justifyContent: 'center', alignItems: 'center' },
  importStepBadgeText: { fontSize: 13, fontFamily: FONTS.medium, color: '#FFFFFF' },
  importStepTitle: { fontSize: 15, fontFamily: FONTS.medium, textAlign: 'right' },
  importStepDesc: { fontSize: 13, fontFamily: FONTS.regular, textAlign: 'right', lineHeight: 20 },
  importTemplateBtn: { flexDirection: 'row-reverse', alignItems: 'center', justifyContent: 'center', gap: 8, borderWidth: 1.5, borderRadius: 8, paddingVertical: 10, paddingHorizontal: 16 },
  importTemplateBtnText: { fontSize: 14, fontFamily: FONTS.medium },
  importUploadBtn: { flexDirection: 'row-reverse', alignItems: 'center', justifyContent: 'center', gap: 8, borderRadius: 8, paddingVertical: 10, paddingHorizontal: 16 },
  importUploadBtnText: { fontSize: 14, fontFamily: FONTS.medium, color: '#FFFFFF' },
  viewModalContent: { width: '100%', maxWidth: 600, borderRadius: 12, overflow: 'hidden' },
  viewModalBody: { padding: 20, gap: 4 },
  viewRow: { justifyContent: 'space-between', alignItems: 'center', paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: '#F1F5F9' },
  viewLabel: { fontSize: 14, fontFamily: FONTS.medium, minWidth: 140 },
  viewValue: { fontSize: 14, fontFamily: FONTS.regular, textAlign: 'left', flex: 1 },
});
